import React, { useState, useEffect } from "react";
import Select from "react-select";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";

function LedgerAutoForm({
  prefillName = "",
  prefillMasterId = "",
  onSuccess = () => { },
  asForm = true,
}) {
  const [name, setName] = useState(prefillName);
  const [masterId, setMasterId] = useState(prefillMasterId);
  const [code, setCode] = useState("");
  const [parentGroupId, setParentGroupId] = useState("19");
  const [description, setDescription] = useState("");
  const [segmentAct, setSegmentAct] = useState("0");
  const [active, setActive] = useState("1");

  const [groups, setGroups] = useState([]);
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const token = localStorage.getItem("accessToken");
  const navigate = useNavigate();

  /* ---------------- Fetch groups ---------------- */
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const res = await api.get("/all-group-list?per_page=100", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGroups(res.data?.data || []);
      } catch {
        setError("Failed to load groups.");
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  /* ---------------- Fetch segments ---------------- */
  useEffect(() => {
    const fetchSegments = async () => {
      setLoadingSegments(true);
      try {
        const res = await api.get("/load-segments", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const formatted =
          res.data?.data?.map((s) => ({
            value: s.id,
            label: `${s.ledger_code} - ${s.ledger_name}`,
          })) || [];
        setSegments(formatted);
      } catch {
        setError("Failed to load segments.");
      } finally {
        setLoadingSegments(false);
      }
    };
    fetchSegments();
  }, []);

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    setError("");
    setSuccess("");

    if (!name.trim()) return setError("Account name is required.");
    if (!code.trim()) return setError("Account code is required.");
    if (!parentGroupId) return setError("Parent Head / Group is required.");

    setSaving(true);
    try {
      const payload = {
        ledger_name: name.trim(),
        master_id: masterId || null,
        ledger_code: code.trim(),
        description: description || null,
        group_id: Number(parentGroupId),
        segment_capability: Number(segmentAct),
        active: Number(active),
        segments: selectedSegments.map((s) => s.value),
        need_voucher: 1,
      };

      const res = await api.post("/ledgers", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess("Account created successfully.");
      onSuccess(res.data.data.response);
      if (!asForm) {
        alert('Account created, please choose it from your dropdown list');
      }

      if (asForm) {
        setTimeout(() => {
          navigate("/ledger", {
            state: { success: "Account created successfully." },
          });
        }, 300);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        "Failed to save ledger."
      );
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Reset ---------------- */
  const handleReset = () => {
    setName(prefillName || "");
    setMasterId(prefillMasterId || "");
    setCode("");
    setParentGroupId("10");
    setDescription("");
    setSegmentAct("0");
    setActive("1");
    setSelectedSegments([]);
    setError("");
    setSuccess("");
  };

  /* ---------------- UI ---------------- */
  const fields = (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label fw-bold">Account Name*</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="col-md-6">
          <label className="form-label fw-bold">Account Code*</label>
          <input className="form-control" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label fw-bold">Parent Head / Group*</label>
          <select className="form-select" value={parentGroupId} onChange={(e) => setParentGroupId(e.target.value)}>
            <option value="">Choose...</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name || g.group_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-bold">Connect Segments</label>
          <Select
            isMulti
            options={segments}
            value={selectedSegments}
            onChange={setSelectedSegments}
            isLoading={loadingSegments}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label fw-bold">Act as Segment</label>
          <select className="form-select" value={segmentAct} onChange={(e) => setSegmentAct(e.target.value)}>
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label fw-bold">Status</label>
          <select className="form-select" value={active} onChange={(e) => setActive(e.target.value)}>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">Description</label>
        <textarea className="form-control" rows="3" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>

      <div className="text-end">
        <button
          type={asForm ? "submit" : "button"}
          className="btn btn-primary me-2"
          disabled={saving}
          onClick={!asForm ? (e) => handleSubmit(e) : undefined}
        >
          {saving ? "Saving..." : "Save"}
        </button>

        <button type="button" className="btn btn-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>
    </>
  );

  return asForm ? (
    <form onSubmit={handleSubmit}>{fields}</form>
  ) : (
    <div>{fields}</div>
  );
}

export default LedgerAutoForm;